import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import {
  api,
  unwrap,
  type CaseDetail,
  type CaseSummary,
  type Permission,
} from "../api/client";
import { hasPermission, useSession } from "../auth/session";
import { utc } from "../import/items";
import { CaseAnalysis } from "./CaseAnalysis";
import { CaseRetention } from "./CaseRetention";
import { Exports } from "./Exports";
import { goTo } from "../nav";

const GRANTABLE: Permission[] = [
  "View",
  "Annotate",
  "RunAnalytics",
  "Export",
  "Delete",
  "ManageCases",
  "ApproveExport",
  "ApproveDisposal",
  "LegalHold",
];

/** `YYYY-MM-DD HH:MM[:SS]` in UTC, or null if it isn't one. */
function parseUtc(text: string): string | null {
  const m = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(:\d{2})?)Z?$/.exec(
    text.trim(),
  );
  if (!m) return null;
  const ms = Date.parse(`${m[1]}T${m[2]}${m[3] ? "" : ":00"}Z`);
  return Number.isNaN(ms) ? null : new Date(ms).toISOString();
}

/**
 * The browser's date-time picker. Its value has no zone; it is read as UTC.
 */
function UtcPicker({
  label,
  value,
  onChange,
  min,
  error,
  helperText,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  min?: string;
  error?: boolean;
  helperText?: string;
}) {
  return (
    <TextField
      size="small"
      type="datetime-local"
      label={label}
      value={value}
      error={error}
      helperText={helperText}
      onChange={(e) => onChange(e.target.value)}
      slotProps={{
        inputLabel: { shrink: true },
        htmlInput: { min: min || undefined, step: 60 },
      }}
    />
  );
}

/**
 * Cases (design §10.3.1): recordings from any team, held by reference, and
 * opened to teams or users by grants that can expire.
 */
export function CasesPage() {
  const session = useSession();
  const cases = useQuery({
    queryKey: ["cases"],
    queryFn: () => unwrap(api.GET("/api/cases")),
  });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const canCreate =
    !!session.data && hasPermission(session.data, "ManageCases");
  const current = selected ?? cases.data?.[0]?.id ?? null;

  return (
    <Stack spacing={2}>
      <Stack direction="row" sx={{ alignItems: "center", gap: 2 }}>
        <Typography variant="h5" component="h2" sx={{ flexGrow: 1 }}>
          Cases
        </Typography>
        {canCreate && (
          <Button variant="contained" onClick={() => setCreating(true)}>
            New case
          </Button>
        )}
      </Stack>
      {cases.isError && <Alert severity="error">{cases.error.message}</Alert>}
      {cases.isSuccess && cases.data.length === 0 && (
        <Alert severity="info">
          {canCreate
            ? "No cases yet. Open one to gather recordings from your teams."
            : "You haven't been given access to any case."}
        </Alert>
      )}
      {!!cases.data?.length && (
        <Stack
          direction={{ xs: "column", md: "row" }}
          sx={{ gap: 2, alignItems: "flex-start" }}
        >
          <Paper
            variant="outlined"
            sx={{ width: { xs: "100%", md: 280 }, flexShrink: 0 }}
          >
            <List dense aria-label="Cases">
              {cases.data.map((c) => (
                <ListItemButton
                  key={c.id}
                  selected={c.id === current}
                  onClick={() => setSelected(c.id)}
                >
                  <ListItemText
                    primary={c.name}
                    secondary={`${c.reference ? `${c.reference} · ` : ""}${c.file_count} file${c.file_count === 1 ? "" : "s"}${c.status === "closed" ? " · closed" : ""}`}
                  />
                </ListItemButton>
              ))}
            </List>
          </Paper>
          {current && <CaseView id={current} />}
        </Stack>
      )}
      {creating && (
        <CaseDialog
          onClose={() => setCreating(false)}
          onSaved={(c) => {
            setSelected(c.id);
            setCreating(false);
          }}
        />
      )}
    </Stack>
  );
}

function CaseDialog({
  existing,
  onClose,
  onSaved,
}: {
  existing?: CaseSummary;
  onClose: () => void;
  onSaved: (c: CaseSummary) => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(existing?.name ?? "");
  const [reference, setReference] = useState(existing?.reference ?? "");
  const [description, setDescription] = useState(existing?.description ?? "");
  const save = useMutation({
    mutationFn: () =>
      existing
        ? unwrap(
            api.PUT("/api/cases/{id}", {
              params: { path: { id: existing.id } },
              body: {
                name,
                description,
                reference: reference || null,
                status: existing.status,
              },
            }),
          )
        : unwrap(
            api.POST("/api/cases", {
              body: { name, description, reference: reference || null },
            }),
          ),
    onSuccess: (c) => {
      void qc.invalidateQueries({ queryKey: ["cases"] });
      onSaved(c);
    },
  });
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{existing ? "Edit case" : "New case"}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
          />
          <TextField
            label="Reference"
            value={reference}
            onChange={(e) => setReference(e.target.value)}
            helperText="e.g. an incident or crime reference"
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          {save.isError && <Alert severity="error">{save.error.message}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!name.trim() || save.isPending}
          onClick={() => save.mutate()}
        >
          {existing ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function CaseView({ id }: { id: string }) {
  const qc = useQueryClient();
  const session = useSession();
  const detail = useQuery({
    queryKey: ["cases", id],
    queryFn: () =>
      unwrap(api.GET("/api/cases/{id}", { params: { path: { id } } })),
  });
  const [editing, setEditing] = useState(false);
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["cases"] });
    void qc.invalidateQueries({ queryKey: ["timeline"] });
  };
  const status = useMutation({
    mutationFn: (next: string) => {
      const c = detail.data?.case;
      if (!c) throw new Error("no case");
      return unwrap(
        api.PUT("/api/cases/{id}", {
          params: { path: { id } },
          body: {
            name: c.name,
            description: c.description,
            reference: c.reference ?? null,
            status: next,
          },
        }),
      );
    },
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (file: string) =>
      unwrap(
        api.DELETE("/api/cases/{id}/files/{file}", {
          params: { path: { id, file } },
        }),
      ),
    onSuccess: refresh,
  });

  if (detail.isError)
    return <Alert severity="error">{detail.error.message}</Alert>;
  if (!detail.data) return null;
  const { case: c, files, grants } = detail.data;
  const manages = c.permissions.includes("ManageCases");
  const open = c.status === "open";

  return (
    <Stack
      spacing={2}
      sx={{ flexGrow: 1, minWidth: 0 }}
      component="section"
      aria-label={c.name}
    >
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          sx={{ alignItems: "center", gap: 1, flexWrap: "wrap" }}
        >
          <Typography variant="h6" component="h3" sx={{ flexGrow: 1 }}>
            {c.name}
            {c.reference && (
              <Typography
                component="span"
                color="text.secondary"
                sx={{ ml: 1 }}
              >
                {c.reference}
              </Typography>
            )}
          </Typography>
          <Chip
            size="small"
            label={open ? "Open" : "Closed"}
            color={open ? "success" : "default"}
          />
          <Button
            size="small"
            onClick={() =>
              goTo(
                `/timeline?case=${c.id}${files[0]?.start_utc ? `&at=${encodeURIComponent(files[0].start_utc)}` : ""}`,
              )
            }
          >
            Show on timeline
          </Button>
          {manages && (
            <>
              <Button size="small" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                size="small"
                disabled={status.isPending}
                onClick={() => status.mutate(open ? "closed" : "open")}
              >
                {open ? "Close case" : "Reopen"}
              </Button>
            </>
          )}
        </Stack>
        {c.description && (
          <Typography sx={{ mt: 1, whiteSpace: "pre-wrap" }}>
            {c.description}
          </Typography>
        )}
        {status.isError && (
          <Alert severity="error">{status.error.message}</Alert>
        )}
      </Paper>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Typography variant="h6" component="h3" gutterBottom>
          Recordings
        </Typography>
        {files.length === 0 ? (
          <Typography color="text.secondary">None yet.</Typography>
        ) : (
          <TableContainer>
            <Table size="small" sx={{ "& .nowrap": { whiteSpace: "nowrap" } }}>
              <TableHead>
                <TableRow>
                  <TableCell>File</TableCell>
                  <TableCell>Team</TableCell>
                  <TableCell>Source</TableCell>
                  <TableCell>Start (UTC)</TableCell>
                  <TableCell>End (UTC)</TableCell>
                  {manages && open && <TableCell />}
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((f) => (
                  <TableRow key={f.id}>
                    <TableCell
                      className="nowrap"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {f.original_name}
                    </TableCell>
                    <TableCell>{f.team_name}</TableCell>
                    <TableCell>{f.source ?? "Unassigned"}</TableCell>
                    <TableCell
                      className="nowrap"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {f.start_utc ? utc(f.start_utc) : "—"}
                    </TableCell>
                    <TableCell
                      className="nowrap"
                      sx={{ fontFamily: "monospace" }}
                    >
                      {f.end_utc ? utc(f.end_utc) : "—"}
                    </TableCell>
                    {manages && open && (
                      <TableCell>
                        <Button
                          size="small"
                          color="error"
                          aria-label={`Remove ${f.original_name}`}
                          disabled={remove.isPending}
                          onClick={() => remove.mutate(f.id)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        {manages && open && <AddRecordings id={id} onAdded={refresh} />}
      </Paper>

      <CaseRetention c={c} />
      <CaseAnalysis c={c} />

      <Exports
        caseId={id}
        canRequest={c.permissions.includes("Export")}
        fileCount={files.length}
      />

      {manages && !session.data?.simple && (
        <Grants id={id} grants={grants} onChange={refresh} />
      )}
      {editing && (
        <CaseDialog
          existing={c}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
    </Stack>
  );
}

function AddRecordings({ id, onAdded }: { id: string; onAdded: () => void }) {
  const libraries = useQuery({
    queryKey: ["libraries"],
    queryFn: () => unwrap(api.GET("/api/libraries")),
  });
  const [library, setLibrary] = useState("");
  const sources = useQuery({
    queryKey: ["libraries", library, "sources"],
    enabled: library !== "",
    queryFn: () =>
      unwrap(
        api.GET("/api/libraries/{id}/sources", {
          params: { path: { id: library } },
        }),
      ),
  });
  const [source, setSource] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [from_, to_] = [parseUtc(from), parseUtc(to)];
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/cases/{id}/files", {
          params: { path: { id } },
          body: {
            file_ids: [],
            range: {
              library_id: library,
              source,
              from: from_ ?? "",
              to: to_ ?? "",
            },
          },
        }),
      ),
    onSuccess: onAdded,
  });
  const ready = library && source && from_ && to_ && from_ < to_;

  return (
    <Stack spacing={1} sx={{ mt: 2 }}>
      <Typography variant="subtitle2" component="h4">
        Add recordings
      </Typography>
      <Stack direction="row" sx={{ gap: 2, flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id={`add-team-${id}`}>From team</InputLabel>
          <Select
            labelId={`add-team-${id}`}
            label="From team"
            value={library}
            onChange={(e) => {
              setLibrary(e.target.value);
              setSource("");
            }}
          >
            {libraries.data?.map((l) => (
              <MenuItem key={l.library_id} value={l.library_id}>
                {l.team_name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 200 }} disabled={!library}>
          <InputLabel id={`add-source-${id}`}>Source</InputLabel>
          <Select
            labelId={`add-source-${id}`}
            label="Source"
            value={source}
            onChange={(e) => setSource(e.target.value)}
          >
            {sources.data?.map((s) => (
              <MenuItem key={s} value={s}>
                {s}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <UtcPicker label="From (UTC)" value={from} onChange={setFrom} />
        <UtcPicker
          label="To (UTC)"
          value={to}
          min={from}
          error={!!from_ && !!to_ && to_ <= from_}
          onChange={setTo}
        />
        <Button
          variant="outlined"
          disabled={!ready || add.isPending}
          onClick={() => add.mutate()}
        >
          Add
        </Button>
      </Stack>
      {add.isError && <Alert severity="error">{add.error.message}</Alert>}
      {add.isSuccess && (
        <Alert severity="success">
          Added {add.data.added} recording{add.data.added === 1 ? "" : "s"}
          {add.data.already ? ` (${add.data.already} already in the case)` : ""}
          .
        </Alert>
      )}
    </Stack>
  );
}

function Grants({
  id,
  grants,
  onChange,
}: {
  id: string;
  grants: CaseDetail["grants"];
  onChange: () => void;
}) {
  const directory = useQuery({
    queryKey: ["directory"],
    queryFn: () => unwrap(api.GET("/api/directory")),
  });
  const [kind, setKind] = useState<"team" | "user">("team");
  const [subject, setSubject] = useState<{ id: string; name: string } | null>(
    null,
  );
  const [perms, setPerms] = useState<Permission[]>(["View"]);
  const [expires, setExpires] = useState("");
  const [expiring, setExpiring] = useState(false);
  const expiresAt = parseUtc(expires);
  const add = useMutation({
    mutationFn: () =>
      unwrap(
        api.POST("/api/cases/{id}/grants", {
          params: { path: { id } },
          body: {
            subject_kind: kind,
            subject_id: subject?.id ?? "",
            permissions: perms,
            expires_at: expiresAt,
          },
        }),
      ),
    onSuccess: () => {
      setSubject(null);
      setExpires("");
      setExpiring(false);
      onChange();
    },
  });
  const revoke = useMutation({
    mutationFn: (grant: string) =>
      unwrap(
        api.DELETE("/api/cases/{id}/grants/{grant}", {
          params: { path: { id, grant } },
        }),
      ),
    onSuccess: onChange,
  });
  const options =
    (kind === "team" ? directory.data?.teams : directory.data?.users) ?? [];

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="h6" component="h3" gutterBottom>
        Access
      </Typography>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Granted to</TableCell>
              <TableCell>Permissions</TableCell>
              <TableCell>Expires (UTC)</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {grants.map((g) => (
              <TableRow key={g.id} sx={{ opacity: g.expired ? 0.6 : 1 }}>
                <TableCell>
                  {g.subject_kind === "team" ? "Team: " : ""}
                  {g.subject_name}
                </TableCell>
                <TableCell>{g.permissions.join(", ")}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>
                  {g.expires_at ? utc(g.expires_at) : "Never"}
                  {g.expired && (
                    <Chip size="small" label="Expired" sx={{ ml: 1 }} />
                  )}
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    color="error"
                    aria-label={`Revoke ${g.subject_name}`}
                    disabled={revoke.isPending}
                    onClick={() => revoke.mutate(g.id)}
                  >
                    Revoke
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {revoke.isError && <Alert severity="error">{revoke.error.message}</Alert>}

      <Stack spacing={1} sx={{ mt: 2 }}>
        <Typography variant="subtitle2" component="h4">
          Grant access
        </Typography>
        <Stack
          direction="row"
          sx={{ gap: 2, flexWrap: "wrap", alignItems: "center" }}
        >
          <ToggleButtonGroup
            size="small"
            exclusive
            value={kind}
            onChange={(_, v: "team" | "user" | null) => {
              if (v) {
                setKind(v);
                setSubject(null);
              }
            }}
          >
            <ToggleButton value="team">Team</ToggleButton>
            <ToggleButton value="user">Person</ToggleButton>
          </ToggleButtonGroup>
          <Autocomplete
            size="small"
            sx={{ minWidth: 220 }}
            options={options}
            value={subject}
            getOptionLabel={(o) => o.name}
            isOptionEqualToValue={(a, b) => a.id === b.id}
            onChange={(_, v) => setSubject(v)}
            renderInput={(params) => (
              <TextField
                {...params}
                label={kind === "team" ? "Team" : "Person"}
              />
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                size="small"
                checked={expiring}
                onChange={(e) => {
                  setExpiring(e.target.checked);
                  if (!e.target.checked) setExpires("");
                }}
              />
            }
            label="Expires"
          />
          {expiring && (
            <UtcPicker
              label="Expires (UTC)"
              value={expires}
              onChange={setExpires}
            />
          )}
        </Stack>
        <Stack direction="row" sx={{ flexWrap: "wrap", columnGap: 1 }}>
          {GRANTABLE.map((p) => (
            <FormControlLabel
              key={p}
              control={
                <Checkbox
                  size="small"
                  checked={perms.includes(p)}
                  onChange={() =>
                    setPerms((cur) =>
                      cur.includes(p)
                        ? cur.filter((x) => x !== p)
                        : [...cur, p],
                    )
                  }
                />
              }
              label={p}
            />
          ))}
        </Stack>
        <Button
          variant="outlined"
          sx={{ alignSelf: "flex-start" }}
          disabled={
            !subject ||
            perms.length === 0 ||
            (expiring && !expiresAt) ||
            add.isPending
          }
          onClick={() => add.mutate()}
        >
          Grant
        </Button>
        {add.isError && <Alert severity="error">{add.error.message}</Alert>}
      </Stack>
    </Paper>
  );
}
